import { API_BASE_URL } from '@/constants/api'
import { toastService } from '@/lib/toastService'
import axios from 'axios'

/**
 * Create Axios instance with base configuration
 * - Base URL from environment
 * - Cookie-based authentication (httpOnly cookies)
 * - CSRF token handling
 */
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important for cookie-based auth
  timeout: 10000, // 10 seconds
})

/**
 * Get CSRF token from cookies
 */
const getCSRFToken = () => {
  const name = 'csrftoken'
  const cookieValue = document.cookie
    .split('; ')
    .find(row => row.startsWith(`${name}=`))
    ?.split('=')[1]
  return cookieValue || ''
}

const fetchCSRFToken = async () => {
  try {
    const response = await apiClient.get('/auth/csrf/', {
      // Skip auth check to prevent infinite loops
      _skipAuthCheck: true,
    })
    // Response contains { csrfToken: '...' } for debugging
    // The actual token is in the cookie which is automatically stored by browser
    return response?.csrfToken
  } catch (error) {
    console.warn('Failed to fetch CSRF token:', error)
    return null
  }
}

/**
 * Request interceptor
 * - Add CSRF token to requests
 * - Add custom headers if needed
 * - Handle FormData Content-Type
 */
apiClient.interceptors.request.use(
  async (config) => {
    // Add CSRF token for non-GET requests
    if (config.method !== 'get') {
      let csrfToken = getCSRFToken()

      // If no CSRF token exists, try to fetch it first (except if this IS the fetch request)
      if (!csrfToken && !config._skipAuthCheck) {
        await fetchCSRFToken()
        csrfToken = getCSRFToken()
      }

      if (csrfToken) {
        config.headers['X-CSRFToken'] = csrfToken
      }
    }

    // Let browser set Content-Type for FormData (multipart/form-data with boundary)
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

/**
 * Response interceptor
 * - Handle API response format
 * - Extract data from standard response
 * - Handle errors globally
 */
apiClient.interceptors.response.use(
  (response) => {
    // API returns: { success, data, message, error, meta, links }
    // Extract the full response to use meta, links, etc.
    return response.data
  },
  (error) => {
    // Handle error response
    if (error.response) {
      // Server responded with error
      const errorData = error.response.data
      const statusCode = error.response.status

      // Don't show toast for expected errors:
      // - 401/403: Authentication/authorization errors (handled by auth context)
      // - 400/404: Client errors (handled by individual components)
      const shouldShowToast = statusCode >= 500

      // Handle specific status codes
      if (shouldShowToast && statusCode >= 500) {
        // Server error (5xx)
        toastService.serverError(errorData?.message || errorData?.error)
      }

      // If API returns standard error format
      if (errorData && errorData.success === false) {
        return Promise.reject(errorData)
      }

      // Otherwise, format error
      return Promise.reject({
        success: false,
        message: errorData?.message || error.message,
        error: {
          code: error.response.status,
          type: error.response.statusText,
          details: errorData,
        },
      })
    }

    // Network error or no response (connection lost, timeout, etc.)
    // Only show toast for real network issues
    if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK' || error.code === 'ETIMEDOUT') {
      toastService.networkError()
    }

    // Return formatted error
    return Promise.reject({
      success: false,
      message: error.message || 'Network error',
      error: {
        code: error.code || 'NETWORK_ERROR',
        type: 'NetworkError',
        details: error,
      },
    })
  }
)

export { apiClient, fetchCSRFToken }

export default apiClient
